import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">Welcome back</h1>
      <p className="text-slate-600 mb-8">Sign in to continue.</p>
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <GoogleAuthButton label="Sign in with Google" />
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px bg-slate-200 flex-1" /> OR <span className="h-px bg-slate-200 flex-1" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
            <p className="text-sm text-slate-600 text-center">
              No account?{" "}
              <Link to="/register" data-testid="login-go-register" className="text-blue-900 font-medium hover:underline">
                Create one
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: params.get("role") === "tradesperson" ? "tradesperson" : "homeowner",
    agreed_to_terms: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed_to_terms) {
      toast.error("Please agree to the Terms of Service to continue.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Fixus!");
      nav(form.role === "tradesperson" ? "/profile/edit" : "/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg = "Sign up failed";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg.replace(/^Value error,\s*/, "");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">Create your account</h1>
      <p className="text-slate-600 mb-8">Join Canada&apos;s home services marketplace. Free for both homeowners and contractors.</p>
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <GoogleAuthButton role={form.role} label={`Sign up with Google as ${form.role === "homeowner" ? "Homeowner" : "Contractor"}`} />
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px bg-slate-200 flex-1" /> OR <span className="h-px bg-slate-200 flex-1" />
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="mb-2 block">I&apos;m signing up as… <span className="text-orange-600">*</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`p-4 rounded-md border cursor-pointer transition-colors ${
                    form.role === "homeowner"
                      ? "border-blue-900 bg-blue-50 ring-2 ring-blue-900/10"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value="homeowner"
                    checked={form.role === "homeowner"}
                    onChange={() => setForm({ ...form, role: "homeowner" })}
                    className="sr-only"
                    data-testid="role-homeowner"
                  />
                  <div className="font-semibold text-slate-900">🏡 Homeowner</div>
                  <div className="text-xs text-slate-500 mt-1">I need work done on my home</div>
                </label>
                <label
                  className={`p-4 rounded-md border cursor-pointer transition-colors ${
                    form.role === "tradesperson"
                      ? "border-blue-900 bg-blue-50 ring-2 ring-blue-900/10"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value="tradesperson"
                    checked={form.role === "tradesperson"}
                    onChange={() => setForm({ ...form, role: "tradesperson" })}
                    className="sr-only"
                    data-testid="role-tradesperson"
                  />
                  <div className="font-semibold text-slate-900">🔧 Contractor</div>
                  <div className="text-xs text-slate-500 mt-1">I&apos;m a tradesperson looking for jobs</div>
                </label>
              </div>
            </div>
            <div>
              <Label htmlFor="name">Full name <span className="text-orange-600">*</span></Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email <span className="text-orange-600">*</span></Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password <span className="text-orange-600">*</span></Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700 pt-2">
              <input
                type="checkbox"
                data-testid="register-agree-terms"
                checked={form.agreed_to_terms}
                onChange={(e) => setForm({ ...form, agreed_to_terms: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-900 focus:ring-blue-900"
              />
              <span>
                I agree to Fixus&apos;s{" "}
                <Link to="/terms" target="_blank" className="text-blue-900 font-medium hover:underline">
                  Terms of Service
                </Link>
                . I understand that Fixus is a listing platform only and is not a party to any agreement between homeowners and contractors, and is not responsible for payments, work quality, or disputes.
              </span>
            </label>
            <Button
              type="submit"
              data-testid="register-submit"
              disabled={loading || !form.agreed_to_terms}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
            >
              {loading ? "Creating account…" : "Create Account"}
            </Button>
            <p className="text-sm text-slate-600 text-center">
              Already have one?{" "}
              <Link to="/login" data-testid="register-go-login" className="text-blue-900 font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
